import ScButton from "@/src/components/ui-kit/button";
import { User, UserRole } from "@/src/models/user.model";
import { useInviteUsers } from "@/src/services/users.blocks.api";
import { useTheme } from "@/src/styles/theme-provider";
import {
  AddCircle,
  ArrowDown2,
  CloseCircle,
  ImportCurve,
} from "iconsax-react-nativejs";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, TextInput, View } from "react-native";
import ImportUsersModal from "./import-user-modal";

type InviteUserModalProps = Readonly<{
  visible: boolean;
  onClose: () => void;
}>;

const FIELD_CONFIG = {
  firstName: {
    placeholder: "LABEL_FIRST_NAME",
    keyboardType: "default",
    autoCapitalize: "sentences",
    errorKey: "ERROR_FIRST_NAME_REQUIRED",
  },
  lastName: {
    placeholder: "LABEL_LAST_NAME",
    keyboardType: "default",
    autoCapitalize: "sentences",
    errorKey: "ERROR_LAST_NAME_REQUIRED",
  },
  email: {
    placeholder: "LABEL_EMAIL_ADDRESS",
    keyboardType: "email-address",
    autoCapitalize: "none",
    errorKey: "ERROR_EMAIL_REQUIRED",
  },
} as const;

const formatRole = (role: string) =>
  role
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const InviteUserModal = ({ visible, onClose }: InviteUserModalProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<User>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const formValues = watch();
  const [inviteList, setInviteList] = useState<User[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const { inviteUsers } = useInviteUsers();

  const handleAdd = (data: User) => {
    setInviteList((previousList) => [
      ...previousList,
      { ...data, role: UserRole.MEMBER },
    ]);
    reset();
  };

  const handleUpdateRole = (index: number, role: UserRole) => {
    setInviteList((previousList) =>
      previousList.map((invitedUser, userIndex) =>
        userIndex === index ? { ...invitedUser, role } : invitedUser,
      ),
    );
    setOpenMenuIndex(null);
  };

  const handleRemoveUser = (index: number) => {
    setInviteList((previousList) =>
      previousList.filter((_invitedUser, userIndex) => userIndex !== index),
    );
  };

  const handleInviteUsers = () => {
    if (inviteList.length === 0) return;
    inviteUsers.mutate(inviteList);
    setInviteList([]);
    onClose();
  };

  const handleImportedUsers = (users: User[]) => {
    if (users.length === 0) return;
    inviteUsers.mutate(users);
    setShowImportModal(false);
    onClose();
  };

  const isAddDisabled =
    !formValues.firstName || !formValues.lastName || !formValues.email;

  return (
    <Modal transparent visible={visible} animationType="fade">
      <Pressable
        style={varStyle}
        className="justify-center items-center bg-shadow-dark flex-1 px-6"
        onPress={onClose}
      >
        <Pressable
          className="rounded-2xl p-6 gap-4 bg-ash w-full max-w-2xl"
          onPress={(e) => e.stopPropagation()}
        >
          <View className="flex-row justify-between items-center">
            <Text className="text-xl font-semibold text-onyx">
              {t("LABEL_INVITE_USER")}
            </Text>
            <Pressable
              onPress={onClose}
              className="rounded-full hover:bg-ash-3"
            >
              <CloseCircle size={24} color={themeVars["--color-ash-2"]} />
            </Pressable>
          </View>

          <View className="flex-col md:flex-row gap-2 md:flex-wrap">
            {(["firstName", "lastName", "email"] as const).map((field) => {
              const config = FIELD_CONFIG[field];
              return (
                <Controller
                  key={field}
                  control={control}
                  name={field}
                  rules={{
                    required: t(config.errorKey),
                    validate:
                      field === "email"
                        ? (fieldValue) => {
                            if (!fieldValue) return true;
                            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                            return (
                              emailRegex.test(fieldValue) ||
                              t("ERROR_INVALID_EMAIL")
                            );
                          }
                        : undefined,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextInput
                      placeholder={t(config.placeholder)}
                      placeholderTextColor={themeVars["--color-slate-default"]}
                      value={value}
                      onChangeText={onChange}
                      keyboardType={config.keyboardType}
                      autoCapitalize={config.autoCapitalize}
                      className="w-full md:flex-1 bg-chalk md:bg-transparent rounded-lg py-3 px-2 md:border md:border-ash-3 text-onyx"
                    />
                  )}
                />
              );
            })}
            <ScButton
              text="BTN_ADD"
              mode="outlined"
              disabled={isAddDisabled}
              disabledClassName="text-gray-dark border-ash-3"
              icon={<AddCircle color={themeVars["--color-primary-default"]} />}
              onPress={handleSubmit(handleAdd)}
            />
          </View>

          <View className="mb-2">
            {errors.firstName && (
              <Text className="text-crimson text-xs">
                {errors.firstName.message}
              </Text>
            )}
            {errors.lastName && (
              <Text className="text-crimson text-xs">
                {errors.lastName.message}
              </Text>
            )}
            {errors.email && (
              <Text className="text-crimson text-xs">
                {errors.email.message}
              </Text>
            )}
          </View>

          {inviteList.length > 0 && (
            <>
              <Text className="text-sm font-medium text-onyx">
                {t("LABEL_ADDED_MEMBERS")} ({inviteList.length})
              </Text>

              {inviteList.map((invitedUser, index) => {
                const isMenuOpen = openMenuIndex === index;
                const rowZIndex = isMenuOpen ? "z-[100]" : "z-[1]";
                const isMemberActive = invitedUser?.role === UserRole.MEMBER;
                const isAdminActive = invitedUser?.role === UserRole.ADMIN;

                return (
                  <View
                    key={invitedUser.email}
                    className={`flex-row items-center justify-between md:border-t md:border-ash-3 px-3 py-2 relative ${rowZIndex}`}
                  >
                    <Text className="flex-1 text-onyx">
                      {invitedUser?.email}
                    </Text>

                    <View className="relative mr-2">
                      <Pressable
                        onPress={() =>
                          setOpenMenuIndex(isMenuOpen ? null : index)
                        }
                        className="flex-row items-center justify-between gap-2 md:border md:border-primary rounded-full px-4 py-1.5 bg-chalk hover:bg-ash-3 hover:rounded-full"
                      >
                        <Text className="text-sm text-primary">
                          {invitedUser?.role
                            ? formatRole(invitedUser.role)
                            : formatRole(UserRole.MEMBER)}
                        </Text>
                        <ArrowDown2
                          size={18}
                          variant="Linear"
                          color={themeVars["--color-primary-default"]}
                        />
                      </Pressable>

                      {isMenuOpen && (
                        <View className="absolute top-10 right-0 bg-chalk md:border md:border-ash-3 rounded-xl overflow-hidden min-w-[120px] shadow-lg">
                          <Pressable
                            onPress={() =>
                              handleUpdateRole(index, UserRole.MEMBER)
                            }
                            className={`px-4 py-3 ${isMemberActive ? "bg-primary-2 rounded-[8px] m-[2px] hover:bg-primary-2" : "hover:bg-ash-3"}`}
                          >
                            <Text
                              className={`text-sm ${isMemberActive ? "text-primary" : "text-onyx"}`}
                            >
                              {t("LABEL_MEMBER")}
                            </Text>
                          </Pressable>

                          <Pressable
                            onPress={() =>
                              handleUpdateRole(index, UserRole.ADMIN)
                            }
                            className={`px-4 py-3 ${isAdminActive ? "bg-primary-2 rounded-[8px] m-[2px] hover:bg-primary-2" : "hover:bg-ash-3"}`}
                          >
                            <Text
                              className={`text-sm ${isAdminActive ? "text-primary" : "text-onyx"}`}
                            >
                              {t("LABEL_ADMIN")}
                            </Text>
                          </Pressable>
                        </View>
                      )}
                    </View>

                    <Pressable
                      onPress={() => handleRemoveUser(index)}
                      className="rounded-full hover:bg-ash-3"
                    >
                      <CloseCircle
                        size={20}
                        color={themeVars["--color-ash-2"]}
                      />
                    </Pressable>
                  </View>
                );
              })}
            </>
          )}

          <View className="flex-row justify-between items-center mt-4">
            <ScButton
              onPress={() => setShowImportModal(true)}
              text="BTN_IMPORT"
              mode="outlined"
              className="rounded"
              icon={
                <ImportCurve color={themeVars["--color-primary-default"]} />
              }
            />
            <ScButton
              text="BTN_INVITE"
              disabled={inviteList.length === 0}
              icon={<AddCircle color="white" />}
              onPress={handleInviteUsers}
              disabledClassName="text-gray-dark border-ash-3"
            />
          </View>
        </Pressable>
      </Pressable>

      <ImportUsersModal
        visible={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleImportedUsers}
      />
    </Modal>
  );
};

export default InviteUserModal;
